import React, { useEffect, useState } from 'react'
import { Button, Input } from '@mobiscroll/react-latest'
import Label from 'src/components/riders/rider-portal/common/Label'
import Skeleton from 'src/components/riders/rider-portal/common/Skeleton'
import AllServicesModal from './AllServicesModal'
import RecentLocationModal from 'src/components/riders/rider-portal/trips/create/Step1/RecentLocationModal'
import SavedLocationItem from 'src/components/riders/rider-portal/trips/create/Step1/SavedLocationItem'
import ServiceItem from 'src/components/riders/rider-portal/trips/create/Step1/ServiceItem'
import LocationPin from 'src/components/icons/ui/LocationPin'
import MapPin from 'src/components/icons/ui/MapPin'
import { ILocationV2 } from 'src/types/location'
import { ISavedLocations, IService } from 'src/types'
import { PORTAL_INPUT_PROPS, RIDER_PORTAL_UI_MESSAGES } from 'src/constants/rider-portal'

interface IServiceAndPickupProps {
  availableServices: IService[]
  fetchingProducts: boolean
  fetchingSavedLocations: boolean
  fetchingServices: boolean
  loading: boolean
  pickUpLocation: ILocationV2 | null
  riderSavedLocations: ISavedLocations[]
  selectedService: IService | null
  handleChangeAddress: (fullAddress: string) => void
  handleMapLabelClick: () => void
  handleSelectRecentLocation: (location: ISavedLocations) => void
  handleSelectService: (service: IService) => void
  setCurrentLocationAsPickup: () => void
}

interface IAvailableServicesProps {
  availableServices: IService[]
  fetchingProducts: boolean
  fetchingServices: boolean
  selectedService: IService | null
  handleSelectService: (service: IService) => void
}

interface IPickupLocationsProps {
  fetchingSavedLocations: boolean
  loading: boolean
  pickUpLocation: ILocationV2 | null
  riderSavedLocations: ISavedLocations[]
  handleChangeAddress: (fullAddress: string) => void
  handleMapLabelClick: () => void
  handleSelectRecentLocation: (location: ISavedLocations) => void
  setCurrentLocationAsPickup: () => void
}

const { common, dataFeedback, trips } = RIDER_PORTAL_UI_MESSAGES

const AvailableServices = ({
  availableServices,
  fetchingProducts,
  fetchingServices,
  selectedService,
  handleSelectService
}: IAvailableServicesProps) => {
  const [open, setOpen] = useState(false)
  const showAllServices = availableServices.length > 4
  const services = showAllServices ? availableServices.filter((service, index) => index <= 3) : availableServices

  useEffect(() => {
    if (fetchingProducts && open) {
      setOpen(false)
    }
  }, [fetchingProducts])

  return (
    <>
      <h3 className='font-black1 my0 pb2 text-center text-lg weight-500'>{trips.availableServices}</h3>
      {fetchingProducts || fetchingServices ? (
        <Skeleton
          text={fetchingProducts ? dataFeedback.retrievingProductsOfSelectedService : dataFeedback.retrievingServices}
          withBg={true}
        />
      ) : services.length > 0 ? (
        <>
          <div className='rp-services-wrapper'>
            {services.map(service => (
              <ServiceItem
                key={service.id}
                selectedService={selectedService}
                service={service}
                handleSelectService={handleSelectService}
              />
            ))}
          </div>
          {showAllServices && (
            <div className='flex justify-end rp-mt4'>
              <i
                className='cursor--pointer font-black2 rp-map-modal-link text-sm'
                role='button'
                tabIndex={0}
                onClick={() => setOpen(true)}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'Enter') {
                    setOpen(true)
                  }
                }}
              >
                {trips.viewAllServices}
              </i>
              <AllServicesModal
                selectedService={selectedService}
                services={availableServices}
                modalTitle={trips.availableServices}
                open={open}
                handleClose={() => setOpen(false)}
                handleSelectService={handleSelectService}
              />
            </div>
          )}
        </>
      ) : (
        <Skeleton text={dataFeedback.noServicesFound} withBg={true} withLoading={false} />
      )}
    </>
  )
}

const PickupLocations = ({
  fetchingSavedLocations,
  loading,
  pickUpLocation,
  riderSavedLocations,
  handleChangeAddress,
  handleMapLabelClick,
  handleSelectRecentLocation,
  setCurrentLocationAsPickup
}: IPickupLocationsProps) => {
  const [open, setOpen] = useState(false)
  const showAllLocations = riderSavedLocations.length > 2
  const locations = showAllLocations ? riderSavedLocations.filter((location, index) => index <= 1) : riderSavedLocations

  const handlePressEnter = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter') {
      setCurrentLocationAsPickup()
    }
  }

  return (
    <>
      <div className='rp-mt24'>
        <div className='text-center'>
          <Label htmlFor={trips.pickupLocation} required={true} text={trips.pickupLocation} />
        </div>
        <div className='column-gap-8 flex items-center mt03'>
          <div className='flex items-center relative w100'>
            <Input
              {...PORTAL_INPUT_PROPS}
              className='rp-input-with-end-btn rp-input-with-start-icon w100'
              value={pickUpLocation && pickUpLocation.fullAddress ? pickUpLocation.fullAddress : ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeAddress(e.target.value)}
            />
            <MapPin className='absolute icon--18x18 rp-ml12 svg-color-green1' />
            <Label
              className='absolute cursor--pointer right-0 rp-map-modal-link rp-mr16'
              htmlFor={common.map}
              text={common.map}
              handleLabelClick={handleMapLabelClick}
            />
          </div>
          <Button
            className='rp-btn-continue rp-current-location-btn'
            disabled={loading}
            onClick={setCurrentLocationAsPickup}
            onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => handlePressEnter(e)}
          >
            <LocationPin className='icon--20x20 svg-color-white' />
          </Button>
        </div>
      </div>
      {!!riderSavedLocations.length && (
        <div className='rp-mt24'>
          <div className='text-center'>
            <Label htmlFor={trips.recentLocations} text={trips.recentLocations} />
          </div>
          <div className=''>
            {fetchingSavedLocations ? (
              <Skeleton text={dataFeedback.retrievingLocations} withBg={true} />
            ) : (
              <>
                {locations.map(location => (
                  <SavedLocationItem
                    key={location.id}
                    location={location}
                    handleSelectRecentLocation={handleSelectRecentLocation}
                  />
                ))}
                {showAllLocations && (
                  <div className='flex justify-end rp-mt4'>
                    <i
                      className='cursor--pointer font-black2 rp-map-modal-link text-sm'
                      role='button'
                      tabIndex={0}
                      onClick={() => setOpen(true)}
                      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setOpen(true)
                        }
                      }}
                    >
                      {trips.viewAllLocations}
                    </i>
                    <RecentLocationModal
                      locations={riderSavedLocations}
                      modalTitle={trips.recentLocations}
                      open={open}
                      handleClose={() => setOpen(false)}
                      handleSelectRecentLocation={location => {
                        setOpen(false)
                        handleSelectRecentLocation(location)
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}

const ServiceAndPickup = ({
  availableServices,
  fetchingProducts,
  fetchingSavedLocations,
  fetchingServices,
  loading,
  pickUpLocation,
  riderSavedLocations,
  selectedService,
  handleChangeAddress,
  handleMapLabelClick,
  handleSelectRecentLocation,
  handleSelectService,
  setCurrentLocationAsPickup
}: IServiceAndPickupProps) => {
  return (
    <>
      <AvailableServices
        availableServices={availableServices}
        fetchingProducts={fetchingProducts}
        fetchingServices={fetchingServices}
        selectedService={selectedService}
        handleSelectService={handleSelectService}
      />
      <div className={`${!selectedService ? 'pointer-events-none opacity-70' : ''}`}>
        <PickupLocations
          fetchingSavedLocations={fetchingSavedLocations}
          loading={loading}
          pickUpLocation={pickUpLocation}
          riderSavedLocations={riderSavedLocations}
          handleChangeAddress={handleChangeAddress}
          handleMapLabelClick={handleMapLabelClick}
          handleSelectRecentLocation={handleSelectRecentLocation}
          setCurrentLocationAsPickup={setCurrentLocationAsPickup}
        />
      </div>
    </>
  )
}

export default ServiceAndPickup



// WEBPACK FOOTER //
// ./src/components/riders/rider-portal/trips/create/Step1/ServiceAndPickup.tsx